import React, { useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import apiService from '../../services/apiService';
import Loader from '../../CommanService/Loader';
import { hasPermission, PERMISSION_CODES, hasPriceAnalysisRFinalizePermission } from '../../utils/permissionUtils';
import { PermissionGuard } from '../../components/PermissionGuard';

const cssLoader = (
  <div style={{
    display: 'flex', 
    flexDirection: 'column',
    alignItems: 'center', 
    justifyContent: 'center', 
    height: '100%',
    gap: '20px'
  }}>
    <div className="bouncing-loader">
      <div></div><div></div><div></div>
    </div>
    <p style={{ 
      color: '#333', 
      fontSize: '18px', 
      fontWeight: '600',
      margin: 0,
      textAlign: 'center',
      textShadow: '0 1px 2px rgba(0,0,0,0.1)'
    }}>
      Data is loading, please wait...
    </p>
    <style>
      {`
      .bouncing-loader {
        display: flex;
        align-items: flex-end;
        justify-content: center;
        height: 60px;
        gap: 10px;
      }
      .bouncing-loader div {
        width: 18px;
        height: 18px;
        background: linear-gradient(135deg, #ff6600 40%, #ffa500 100%);
        border-radius: 50%;
        animation: bouncing-loader 0.6s infinite alternate;
        box-shadow: 0 4px 16px rgba(255, 102, 0, 0.2);
      }
      .bouncing-loader div:nth-child(2) {
        animation-delay: 0.2s;
      }
      .bouncing-loader div:nth-child(3) {
        animation-delay: 0.4s;
      }
      @keyframes bouncing-loader {
        to {
          transform: translateY(-30px) scale(1.1);
          box-shadow: 0 16px 32px rgba(255, 102, 0, 0.3);
        }
      }
      `}
    </style>
  </div>
);

const RApplicationComponent = () => {
  const [loading, setLoading] = useState(true);
  const [finalizeLoading, setFinalizeLoading] = useState(false);
  const companyid = JSON.parse(sessionStorage.getItem('user')).company_id; // TODO: Replace with dynamic value if needed
  const userid = JSON.parse(sessionStorage.getItem('user')).id; // Get user id
  
  // Get user from Redux store for permission checking
  const user = useSelector(state => state.auth.user) || JSON.parse(sessionStorage.getItem('user'));
  
  // Permission check for Finalize button
  const canFinalize = hasPriceAnalysisRFinalizePermission(user);

  // State for iframe URL

  //Deploy for local instance     
  //const [iframeUrl, setIframeUrl] = useState(`http://127.0.0.1:4745/?companyid=${companyid}&id=${userid}`);
  const [iframeUrl, setIframeUrl] = useState(`https://solutions3.baryons.net/dev_pricing_app/?companyid=${companyid}&id=${userid}`);
  //Deploy for Dev instance
   //const [iframeUrl, setIframeUrl] = useState(`http://127.0.0.1:4407/?companyid=${companyid}&id=${userid}`);
  //Deploy for UAT instance     
  //const [iframeUrl, setIframeUrl] = useState(`http://10.206.1.22:8081/?companyid=${companyid}&id=${userid}`);

  //Deploy for Stage - 2 instance 
  // const [iframeUrl, setIframeUrl] = useState(`https://solutions3.baryons.net/pricing_app_stage2/?companyid=${companyid}&id=${userid}`);

  // Dropdown state
  const [filterOptions, setFilterOptions] = useState([]);
  const [filterValue, setFilterValue] = useState('');
  const [selectedAnalysisId, setSelectedAnalysisId] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const inputRef = useRef(null);

  // Fetch filter options from API on mount and every 30 seconds
  React.useEffect(() => {
    let isMounted = true;
    const fetchConfig = () => {
      apiService.GetSystemConfigByUSerid(userid)
        .then(res => {
          if (!isMounted) return;
          if (res && Array.isArray(res.data)) {
            setFilterOptions(res.data.map(item => ({ name: item.filter_name, id: item.id })));
          } else {
            setFilterOptions([]);
          }
        })
        .catch(error => {
          if (!isMounted) return;
          setFilterOptions([]);
          console.error('Error fetching SystemConfigByUSerid:', error);
        });
    };
    fetchConfig();
    const interval = setInterval(fetchConfig, 30000); // 30 seconds
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [userid]);
  // Filter options by name
  const filteredOptions = filterOptions.filter(option =>
    option.name.toLowerCase().includes(filterValue.toLowerCase())
  );


  // Close dropdown on outside click
  React.useEffect(() => {
    function handleClickOutside(event) {
      if (inputRef.current && !inputRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle option selection
  const handleOptionSelect = (option) => {
    setFilterValue(option.name);
    setSelectedAnalysisId(option.id);
    setDropdownOpen(false);
    setLoading(true);
    //Local
    //setIframeUrl(`http://127.0.0.1:4745/?companyid=${companyid}&id=${userid}&filterid=${encodeURIComponent(option.id)}`);

    //Deploy for Dev instance
    setIframeUrl(`https://solutions3.baryons.net/dev_pricing_app/?companyid=${companyid}&id=${userid}&filterid=${encodeURIComponent(option.id)}`);

    //UAT
    //setIframeUrl(`http://10.206.1.22:8081/?companyid=${companyid}&id=${userid}&filterid=${encodeURIComponent(option.id)}`);

    //Deploy for Stage - 2 instance
    //setIframeUrl(`https://solutions3.baryons.net/pricing_app_stage2/?companyid=${companyid}&id=${userid}&filterid=${encodeURIComponent(option.id)}`);
  };

  // Function to create modal dialog
  const createModal = (title, message, isError = false) => {
    const modalOverlay = document.createElement('div');
    modalOverlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: flex-start;
      justify-content: center;
      padding-top: 8%;
      z-index: 9999;
    `;
    
    const modalDialog = document.createElement('div');
    modalDialog.style.cssText = `
      background: white;
      border-radius: 0px;
      box-shadow: 0 4px 16px rgba(0,0,0,0.2);
      padding: 20px;
      min-width: 450px;
      max-width: 550px;
      margin: 20px;
    `;
    
    const modalTitle = document.createElement('h3');
    modalTitle.textContent = title;
    modalTitle.style.cssText = `
      margin: 0 0 15px 0;
      color: ${isError ? '#dc3545' : '#333'};
      font-size: 18px;
      font-weight: 600;
      text-align: left;
    `;
    
    const modalMessage = document.createElement('p');
    modalMessage.textContent = message;
    modalMessage.style.cssText = `
      margin: 0 0 20px 0;
      color: #666;
      font-size: 14px;
      line-height: 1.4;
      text-align: left;
    `;
    
    const dismissButton = document.createElement('button');
    dismissButton.textContent = 'Dismiss';
    dismissButton.style.cssText = `
      background: #f8f9fa;
      color: #495057;
      border: 1px solid #dee2e6;
      padding: 8px 20px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;
      font-weight: 500;
      transition: all 0.2s ease;
      box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    `;
    
    // Add hover effect
    dismissButton.onmouseover = () => {
      dismissButton.style.background = '#e9ecef';
      dismissButton.style.borderColor = '#adb5bd';
    };
    dismissButton.onmouseout = () => {
      dismissButton.style.background = '#f8f9fa';
      dismissButton.style.borderColor = '#dee2e6';
    };
    
    // Close modal when button is clicked
    dismissButton.onclick = () => {
      document.body.removeChild(modalOverlay);
    };
    
    // Close modal when clicking outside
    modalOverlay.onclick = (e) => {
      if (e.target === modalOverlay) {
        document.body.removeChild(modalOverlay);
      }
    };
    
    // Create a container for the button to align it to the right
    const buttonContainer = document.createElement('div');
    buttonContainer.style.cssText = `
      display: flex;
      justify-content: flex-end;
      margin-top: 20px;
    `;
    buttonContainer.appendChild(dismissButton);
    
    // Assemble modal
    modalDialog.appendChild(modalTitle);
    modalDialog.appendChild(modalMessage);
    modalDialog.appendChild(buttonContainer);
    modalOverlay.appendChild(modalDialog);
    
    // Add to page
    document.body.appendChild(modalOverlay);
  };

  // Handle finalize button click
  const handleFinalize = async () => {
    // Check permission before proceeding
    if (!canFinalize) {
      createModal('Error', 'You do not have permission to finalize analysis.', true);
      return;
    }

    try {
      setFinalizeLoading(true); // Show loader
      
      // Use the stored selected analysis ID
      if (!selectedAnalysisId) {
        console.error('No analysis selected');
        createModal('Error', 'No analysis selected', true);
        setFinalizeLoading(false); // Hide loader
        return;
      }

      // Validate required values
      if (!selectedAnalysisId) {
        console.error('Analysis ID is missing');
        createModal('Error', 'Analysis ID is missing', true);
        setFinalizeLoading(false); // Hide loader
        return;
      }

      if (!userid) {
        console.error('User ID is missing');
        createModal('Error', 'User ID is missing', true);
        setFinalizeLoading(false); // Hide loader
        return;
      }

      // Call the finalize API
      const response = await apiService.FinalizeAnalysis(selectedAnalysisId, userid, userid);
      
      if (response.success) {
        console.log('Analysis finalized successfully');
        createModal('Success', 'Analysis finalized successfully.');
        // Optionally refresh the filter options
      } else {
        console.error('Failed to finalize analysis:', response.message);
        createModal('Error', `Failed to finalize analysis: ${response.message}`, true);
      }
    } catch (error) {
      console.error('Error finalizing analysis:', error);
      createModal('Error', `Error finalizing analysis: ${error.message}`, true);
    } finally {
      setFinalizeLoading(false); // Hide loader
    }
  };

  return (
    <div style={{ width: '100%', height: '100vh', margin: 0, padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      {/* COMMENTED OUT: Search analysis dropdown */}
      {/* <div style={{ background: '#fff', padding: '16px 24px', fontSize: 15, fontWeight: 600, borderBottom: '1px solid #eee', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>Search here to get your previous Analysis</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', position: 'relative' }} ref={inputRef}>
          <label htmlFor="filter-analysis" style={{ fontWeight: 600, marginRight: 8, fontSize: 16 }}>Filter by:</label>
          <input
            id="filter-analysis"
            name="filter-analysis"
            placeholder="Search Analysis"
            style={{
              fontSize: 16,
              padding: '6px 18px',
              borderRadius: '6px',
              border: '1px solid #ccc',
              outline: 'none',
              minWidth: 180
            }}
            value={filterValue}
            onChange={e => {
              setFilterValue(e.target.value);
              setSelectedAnalysisId(null); // Clear selected ID when user types
              setDropdownOpen(true);
            }}
            onFocus={() => setDropdownOpen(true)}
            autoComplete="off"
          />
          <PermissionGuard permission={PERMISSION_CODES.PRICE_ANALYSIS_R_FINALIZE}>
            <button
              onClick={handleFinalize}
              disabled={!filterValue || finalizeLoading}
              style={{
                fontSize: 16,
                padding: '8px 20px',
                borderRadius: '6px',
                border: 'none',
                backgroundColor: filterValue && !finalizeLoading ? '#ff6600' : '#cccccc',
                color: 'white',
                fontWeight: 600,
                cursor: filterValue && !finalizeLoading ? 'pointer' : 'not-allowed',
                transition: 'background-color 0.2s ease',
                opacity: filterValue && !finalizeLoading ? 1 : 0.6
              }}
              onMouseOver={(e) => {
                if (filterValue && !finalizeLoading) {
                  e.target.style.backgroundColor = '#e55a00';
                }
              }}
              onMouseOut={(e) => {
                if (filterValue && !finalizeLoading) {
                  e.target.style.backgroundColor = '#ff6600';
                }
              }}
            >
              {finalizeLoading ? 'Finalizing...' : 'Finalize'}
            </button>
          </PermissionGuard>
          {dropdownOpen && filteredOptions.length > 0 && (
            <div
              style={{
                position: 'absolute',
                top: 38,
                left: 120,
                width: 220,
                maxHeight: 180,
                overflowY: 'auto',
                background: '#fff',
                border: '1px solid #ccc',
                borderRadius: '6px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                zIndex: 10
              }}
            >
              {filteredOptions.map((option, idx) => (
                <div
                  key={option.id}
                  style={{
                    padding: '8px 16px',
                    cursor: 'pointer',
                    background: filterValue === option.name ? '#f0f4fa' : '#fff',
                    fontWeight: 500
                  }}
                  onMouseDown={() => handleOptionSelect(option)}
                >
                  {option.name}
                </div>
              ))}
            </div>
          )}
        </div>
      </div> */}
      {finalizeLoading && <Loader />}
      <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        {loading && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(255,255,255,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2
          }}>
            {cssLoader}
          </div>
        )}
        <iframe
          src={iframeUrl}
          title="R Application"
          style={{ width: '100%', height: '100%', border: 'none' }}
          allowFullScreen
          onLoad={() => setLoading(false)}
        />
      </div>
    </div>
  );
};

export default RApplicationComponent; 